// Item views (IMPLEMENTATION §2.9 "Views"): one pooled sprite per loose floor item and per chute carry,
// reconciled with the state every frame (a view appears / moves / goes with its item). Only chute carries
// are interpolated (RenderSync, render/sync.ts); the board FX (M10, render/juice/boardFx.ts) animate a
// per-view anim record — offset, scale x / y, alpha, angle — never the item's cell, so the board always shows
// where the sim says an item is. A frame is two passes: `reconcile` (views follow the state; a view whose
// item left is remembered for this frame as `gone`, so a merge can converge from where it was drawn) and,
// after the FX stepped, `draw` (the anim applied: the view's alpha IS the FX alpha, M10 review fix).
import { frameId } from '../../../art/catalog.ts';
import type { SimDefs } from '../../../sim/defs.ts';
import type { ChuteB, SimState } from '../../../sim/state.ts';
import { type Anim, type GoneFx, type ItemFx, resetAnim, restAnim } from '../../juice/boardFx.ts';
import { DEPTH } from '../../layers.ts';
import { carryT, carryWorld } from '../../sync.ts';
import type { Spr, SprPool } from '../Spr.ts';

export type { Anim };

interface ItemView {
  spr: Spr;
  gen: number;
  carry: boolean;
  frame: string;
  /** World centre without the anim offset. */
  wx: number;
  wy: number;
  anim: Anim;
}

export class ItemViews {
  private readonly views = new Map<number, ItemView>();
  private readonly dropped = new Map<number, GoneFx>();
  private gen = 0;
  private carries = 0;
  private readonly pool: SprPool;
  private readonly wu: number;
  /** M6 input overlay: the dragged item (hidden while its DOM ghost flies) and the lifted selection. */
  private hidden = -1;
  /**
   * The item hidden up to the previous frame: a successful drop clears the overlay in the same pointerup task
   * as the drop, so the frame that drops the dragged item's view already has hidden = none; the view was still
   * hidden under the DOM ghost when it was last drawn (a drag merge pops in place, a hand insert is swallowed
   * where it lands: M10 decision 9).
   */
  private prevHidden = -1;
  private liftId = -1;
  private liftWu = 0;

  constructor(pool: SprPool, cellWu: number) {
    this.pool = pool;
    this.wu = cellWu;
  }

  private view(id: number, frame: string, carry: boolean): ItemView {
    let v = this.views.get(id);
    if (v === undefined) {
      v = {
        spr: this.pool.acquire(frame, DEPTH.item),
        gen: this.gen,
        carry,
        frame,
        wx: 0,
        wy: 0,
        anim: restAnim(),
      };
      this.views.set(id, v);
    } else if (v.frame !== frame) {
      this.pool.frame(v.spr, frame);
      v.frame = frame;
    }
    v.gen = this.gen;
    v.carry = carry;
    return v;
  }

  /** M6: hide item `hidden` (null = none) and lift item `liftId` by `liftWu` world units. */
  setOverlay(hidden: number | null, liftId: number | null, liftWu: number): void {
    this.prevHidden = this.hidden;
    this.hidden = hidden ?? -1;
    this.liftId = liftId ?? -1;
    this.liftWu = liftWu;
  }

  /** Reconciles the views with the floor items and chute carries of `s` (no drawing yet). */
  reconcile(s: SimState, d: SimDefs, alpha: number): void {
    this.gen += 1;
    const wu = this.wu;
    for (const it of s.floor) {
      const v = this.view(it.id, frameId.item(it.fam, it.tier), false);
      v.wx = (it.x + 0.5) * wu;
      v.wy = (it.y + 0.5) * wu;
    }
    const hop = d.buildings.chute.hopTicks;
    let carries = 0;
    for (const b of s.buildings) {
      if (b.type !== 'chute') continue;
      const ch = b as ChuteB;
      if (ch.carry === null) continue;
      const v = this.view(ch.carry.id, frameId.item(ch.carry.fam, ch.carry.tier), true);
      const p = carryWorld(ch, carryT(ch.prog, alpha, hop), wu);
      v.wx = p.x;
      v.wy = p.y;
      carries += 1;
    }
    this.carries = carries;
    const stale: number[] = [];
    for (const [id, v] of this.views) if (v.gen !== this.gen) stale.push(id);
    for (const id of stale) this.drop(id, true);
  }

  /** Draws every view with its anim (after the FX stepped this frame). */
  draw(): void {
    for (const [id, v] of this.views) {
      const a = v.anim;
      const lift = id === this.liftId ? this.liftWu : 0;
      v.spr.place(v.wx + a.dx, v.wy + a.dy - lift, this.wu * a.sx, this.wu * a.sy);
      v.spr.angle(a.rot);
      v.spr.alpha(id === this.hidden ? 0 : a.a < 0 ? 0 : a.a > 1 ? 1 : a.a);
    }
  }

  /** Views dropped this frame are forgotten (call after the FX handled the frame's events). */
  endFrame(): void {
    if (this.dropped.size > 0) this.dropped.clear();
  }

  private drop(id: number, remember: boolean): void {
    const v = this.views.get(id);
    if (v === undefined) return;
    if (remember) {
      const lift = id === this.liftId ? this.liftWu : 0;
      this.dropped.set(id, {
        x: v.wx + v.anim.dx,
        y: v.wy + v.anim.dy - lift,
        frame: v.frame,
        hidden: id === this.hidden || id === this.prevHidden,
      });
    }
    this.pool.release(v.spr);
    this.views.delete(id);
  }

  /** The FX handle of item `id` (its anim record and rest centre), or null when it has no view. */
  fx(id: number): ItemFx | null {
    const v = this.views.get(id);
    return v === undefined ? null : { anim: v.anim, x: v.wx, y: v.wy };
  }

  /** Where item `id` was drawn when its view was dropped this frame, or null. */
  gone(id: number): GoneFx | null {
    return this.dropped.get(id) ?? null;
  }

  /** The anim record of item `id` (tweens write into it), or null when it has no view. */
  anim(id: number): Anim | null {
    return this.views.get(id)?.anim ?? null;
  }

  /** World centre as drawn (offset included), or null. */
  worldPos(id: number): { x: number; y: number } | null {
    const v = this.views.get(id);
    return v === undefined ? null : { x: v.wx + v.anim.dx, y: v.wy + v.anim.dy };
  }

  /** Every anim back to rest (a rebuild: the FX were cleared). */
  restAll(): void {
    for (const v of this.views.values()) resetAnim(v.anim);
  }

  clear(): void {
    for (const id of [...this.views.keys()]) this.drop(id, false);
    this.dropped.clear();
  }

  get count(): number {
    return this.views.size;
  }

  get carryCount(): number {
    return this.carries;
  }
}
